/**
 * Lazy loading of translation bundles for the i18next backend.
 *
 * Lives apart from ./config.ts on purpose: importing that module initialises a
 * live i18next instance, so anything it holds is unreachable from a test that
 * does not want the side effect (the global test setup replaces the whole
 * module for exactly that reason).
 */

// i18next asks for resources one namespace at a time. Answering each ask with
// its own dynamic import cost 15 requests per language on first paint — enough,
// together with the route chunks, to trip a zone rate-limit rule and lock the
// user out of the dashboard (2026-07-22). One import per LANGUAGE instead: the
// locale's bundle carries all of its namespaces (see ./aggregate.ts), and the
// 14 remaining asks resolve off the same in-flight promise.
const languageBundles = new Map<string, Promise<Record<string, unknown>>>();

export function loadLanguageBundle(language: string): Promise<Record<string, unknown>> {
  let bundle = languageBundles.get(language);
  if (!bundle) {
    bundle = import(`./locales/${language}.bundle.ts`)
      .then((module) => module.default as Record<string, unknown>)
      // Don't cache a failure: a dropped chunk request must stay retriable,
      // otherwise one bad network moment leaves the UI untranslated for the
      // whole session.
      .catch((error: unknown) => {
        languageBundles.delete(language);
        throw error;
      });
    languageBundles.set(language, bundle);
  }
  return bundle;
}

/**
 * Answer one of i18next's namespace reads out of the language bundle.
 *
 * The second callback argument is i18next's RETRY flag, not payload:
 * `if (err && data && tried < maxRetries)` in BackendConnector.read. Which
 * failure gets the flag decides whether the UI can heal itself:
 *
 *   - bundle failed to load (dropped chunk, offline blip) → retriable, so the
 *     five built-in retries with exponential backoff get to run;
 *   - bundle loaded but the namespace is absent → not retriable, the file is
 *     simply not there and five more asks only delay the same raw keys.
 *
 * Answering (error, null) for the first case — which is what this did — opted
 * the namespace out of every retry, so one dropped chunk left it rendering raw
 * keys for the rest of the session and nothing ever asked again.
 */
export function readNamespaceBundle(
  language: string,
  namespace: string,
  callback: (error: unknown, data: unknown) => void
): void {
  loadLanguageBundle(language)
    .then((resources) => {
      const data = resources[namespace];
      if (data === undefined) {
        // A namespace listed in `ns` with no JSON file behind it. i18next would
        // otherwise render raw keys with no hint as to why.
        callback(
          new Error(`Missing namespace "${namespace}" for language "${language}"`),
          null
        );
        return;
      }
      callback(null, data);
    })
    .catch((error) => {
      callback(error, true);
    });
}
